import {useEffect, useId} from 'react';
import {FormHelperText} from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Select,
  Stack,
  TextField,
} from '@wellstone-solutions/web';
import {halfWidthSx, useNoteForm} from '../../../NoteForm';
import {OTHER_PLACE_OF_SERVICE} from '../../choices';
import type {ContactNoteAction, ContactNoteState} from '../../state';
import {usePlaceOfServiceCatalog} from './usePlaceOfServiceCatalog';

export const PlaceOfServiceField = () => {
  const {state, dispatch} = useNoteForm<ContactNoteState, ContactNoteAction>();
  const {placeOfService, dateOfService} = state;
  const catalogQuery = usePlaceOfServiceCatalog(dateOfService);
  const options = catalogQuery.data?.placesOfService;
  const pickedCode = placeOfService?.code;
  const helperTextId = useId();
  const isListed = (code: string) =>
    !!options?.some((place) => place.code === code);

  // A new date of service can drop or rename the current pick
  useEffect(() => {
    if (!options || !placeOfService) {
      return;
    }
    const current = options.find(({code}) => code === placeOfService.code);
    if (!current) {
      dispatch({type: 'set', field: 'placeOfService', value: null});
    } else if (
      placeOfService.confirmedFor !== dateOfService ||
      placeOfService.label !== current.label
    ) {
      dispatch({
        type: 'set',
        field: 'placeOfService',
        value: {...current, confirmedFor: dateOfService},
      });
    }
  }, [options, placeOfService, dateOfService, dispatch]);

  const pickOf = (code: string) => {
    const option = options?.find((place) => place.code === code);
    return option ? {...option, confirmedFor: dateOfService} : null;
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
        <Box sx={halfWidthSx}>
          <Select
            label="Place of service"
            items={(options ?? []).map(({code, label}) => ({
              label,
              value: code,
            }))}
            // Blank until the effect above clears a dropped pick, so the list never shows a code it lacks
            value={pickedCode && isListed(pickedCode) ? pickedCode : ''}
            canUnselect={false}
            aria-describedby={helperTextId}
            containerProps={{
              required: true,
              fullWidth: true,
              disabled: !options,
            }}
            onChange={(event) =>
              dispatch({
                type: 'set',
                field: 'placeOfService',
                value: pickOf(event.target.value),
              })
            }
          />
          <FormHelperText id={helperTextId}>
            Where the member was located at time of service.
          </FormHelperText>
        </Box>
        {pickedCode === OTHER_PLACE_OF_SERVICE && (
          <TextField
            required
            label="Other place of service (specify)"
            value={state.placeOfServiceOther}
            inputProps={{maxLength: 100}}
            onChange={(event) =>
              dispatch({
                type: 'set',
                field: 'placeOfServiceOther',
                value: event.target.value,
              })
            }
            sx={{flex: 1}}
          />
        )}
      </Stack>
      {catalogQuery.isError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => catalogQuery.refetch()}>
              Try again
            </Button>
          }>
          Couldn't load places of service.
        </Alert>
      )}
    </Stack>
  );
};
